import { Theme, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { NumberSerialiserOptions, Serialiser } from "hooks/serialisers/types";
import type {
  Annotations,
  BoxPlotData,
  Layout as PlotlyLayout,
} from "plotly.js";
import Plot from "components/plotly/LocalisedPlot";
import {
  ImpuritiesPlotProps,
  ImpuritiesConstraint,
  generateBasePlotLayoutStructure,
  getXAxisId,
  getYAxisId,
} from "./ImpuritiesPlotCommon";

const generateBoxPlotData = (
  constraints: ImpuritiesConstraint[],
  theme: Theme
): Partial<BoxPlotData>[] => {
  const data = constraints.flatMap((constraint, i) => {
    const xAxisId = getXAxisId(i);
    const yAxisId = getYAxisId(i);
    const { name, quantiles } = constraint;
    const boxData = {
      type: "box",
      x: [""],
      orientation: "v",
      name,
      marker: {
        color: Object.values(theme.palette.data)[
          i % Object.values(theme.palette.data).length
        ],
      },
      xaxis: xAxisId,
      yaxis: yAxisId,
      width: 0.4,
      lowerfence: quantiles[0.05] !== undefined ? [quantiles[0.05]] : [],
      q1: quantiles[0.25] !== undefined ? [quantiles[0.25]] : [],
      q3: quantiles[0.75] !== undefined ? [quantiles[0.75]] : [],
      median: quantiles[0.5] !== undefined ? [quantiles[0.5]] : [],
      upperfence: quantiles[0.95] !== undefined ? [quantiles[0.95]] : [],
    } as Partial<BoxPlotData>;

    return boxData;
  });

  return data;
};

interface GenerateBoxPlotLayoutProps {
  constraints: ImpuritiesConstraint[];
  theme: Theme;
  height?: number;
  t: (key: string) => string;
  serialiser: Serialiser<number, NumberSerialiserOptions<never>>;
}
const generateBoxPlotLayout = ({
  constraints,
  theme,
  height,
  t,
  serialiser,
}: GenerateBoxPlotLayoutProps): Partial<PlotlyLayout> => {
  const getLargestQuantile = (constraint: ImpuritiesConstraint) =>
    Math.max(...Object.values(constraint.quantiles));
  const layout = generateBasePlotLayoutStructure({
    constraints,
    theme,
    getMaxValueRendered: getLargestQuantile,
    height,
  });

  const boxAnnotations: Partial<Annotations>[] = constraints.flatMap(
    (constraint, i) => {
      const xAxisId = getXAxisId(i);
      const yAxisId = getYAxisId(i);

      const expectedFailureRateAnnotation: Partial<Annotations>[] = [
        {
          text: `↑ ${serialiser.format(
            constraint.expectedOverToleranceRatePercent
          )}%`,
          x: 0.4,
          y: constraint.weightPercentMaxTolerance,
          xref: xAxisId,
          yref: yAxisId,
          showarrow: false,
          yanchor: "bottom",
          xanchor: "right",
          font: { size: 12 },
        },
        {
          text: t("failureRate"),
          x: 0.4,
          y: constraint.weightPercentMaxTolerance,
          xref: xAxisId,
          yref: yAxisId,
          showarrow: false,
          yanchor: "top",
          xanchor: "right",
          font: { size: 12 },
        },
        ...((constraint.weightPercentMinTolerance !== null &&
        constraint.weightPercentMinTolerance > 0
          ? [
              {
                text: `↓ ${serialiser.format(
                  constraint.expectedUnderToleranceRatePercent
                )}%`,
                x: 0.4,
                y: constraint.weightPercentMinTolerance,
                xref: xAxisId,
                yref: yAxisId,
                showarrow: false,
                yanchor: "top",
                xanchor: "right",
                font: { size: 12 },
              },
            ]
          : []) as Partial<Annotations>[]),
      ];
      const hasMin =
        constraint.weightPercentMinTolerance !== null &&
        constraint.weightPercentMinTolerance > 0;
      if (hasMin) {
        const lineMeanings: Partial<Annotations>[] = [
          {
            text: t("maxPercent"),
            x: -0.4,
            y: constraint.weightPercentMaxTolerance,
            xref: xAxisId,
            yref: yAxisId,
            showarrow: false,
            xanchor: "left",
            yanchor: "top",
            font: { color: theme.palette.text.secondary, size: 10 },
          },
          {
            text: t("minPercent"),
            x: -0.4,
            y: constraint.weightPercentMinTolerance ?? undefined,
            xref: xAxisId,
            yref: yAxisId,
            showarrow: false,
            xanchor: "left",
            yanchor: "top",
            font: { color: theme.palette.text.secondary, size: 10 },
          },
        ];
        return [...expectedFailureRateAnnotation, ...lineMeanings];
      }
      return expectedFailureRateAnnotation;
    }
  );

  layout.annotations = [...(layout.annotations ?? []), ...boxAnnotations];

  return layout;
};

export const ImpuritiesBoxPlot = ({
  constraints,
  height,
}: ImpuritiesPlotProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const serialiser = useNumberSerialiser({
    decimalPlaces: 3,
  });

  const data = generateBoxPlotData(constraints, theme);
  const layoutPlotly: Partial<PlotlyLayout> = generateBoxPlotLayout({
    constraints,
    height,
    theme,
    t,
    serialiser,
  });

  return (
    <Plot
      useResizeHandler
      style={{ width: "100%" }}
      data={data}
      layout={layoutPlotly}
      config={{ responsive: true, displaylogo: false, autosizable: true }}
    />
  );
};
